import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BookMarked, LogIn, UserPlus, Loader2, ArrowLeft } from "lucide-react";
import LogoMR from "@/components/landing/LogoMR";
import { bsLogin, bsRegister, bsGetToken, bsFormatError } from "@/lib/bookstoreApi";

export default function LibreriePage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [reg, setReg] = useState({
    business_name: "",
    contact_person: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    province: "",
    vat_number: "",
    tax_code: "",
    website: "",
    notes: "",
  });

  useEffect(() => {
    if (bsGetToken()) navigate("/librerie/dashboard", { replace: true });
  }, [navigate]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await bsLogin(email, password);
      toast.success("Accesso effettuato");
      navigate("/librerie/dashboard");
    } catch (err) {
      toast.error(bsFormatError(err));
    } finally {
      setLoading(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await bsRegister(reg);
      toast.success(`Benvenuti ${data.bookstore.business_name}!`);
      navigate("/librerie/dashboard");
    } catch (err) {
      toast.error(bsFormatError(err));
    } finally {
      setLoading(false);
    }
  };

  const setR = (k, v) => setReg((s) => ({ ...s, [k]: v }));

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="librerie-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate("/")}
          className="inline-flex items-center gap-2 text-sm text-[#0B192C] hover:text-[#0E7C8F] mb-8"
          data-testid="back-home-btn"
        >
          <ArrowLeft size={16} /> Torna al sito
        </button>

        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <LogoMR size={72} />
          </div>
          <h1 className="font-serif-display text-4xl md:text-5xl text-[#0B192C] mb-3">
            Area Riservata Librerie
          </h1>
          <p className="text-base text-[#5A6472] max-w-2xl mx-auto">
            Portale digitale per le librerie partner: gestisci il conto vendita,
            richiedi copie del libro e ottieni il tuo codice sconto 5% da girare.
          </p>
        </div>

        <div className="max-w-md mx-auto mb-6">
          <div className="grid grid-cols-2 border border-[#0B192C]" data-testid="mode-tabs">
            <button
              onClick={() => setMode("login")}
              data-testid="mode-login"
              className={`py-3 text-xs uppercase tracking-[0.18em] transition-colors ${
                mode === "login"
                  ? "bg-[#0B192C] text-[#F9F8F6]"
                  : "bg-transparent text-[#0B192C] hover:bg-[#0B192C]/5"
              }`}
            >
              <LogIn className="inline w-3 h-3 mr-2" /> Accedi
            </button>
            <button
              onClick={() => setMode("register")}
              data-testid="mode-register"
              className={`py-3 text-xs uppercase tracking-[0.18em] transition-colors ${
                mode === "register"
                  ? "bg-[#0B192C] text-[#F9F8F6]"
                  : "bg-transparent text-[#0B192C] hover:bg-[#0B192C]/5"
              }`}
            >
              <UserPlus className="inline w-3 h-3 mr-2" /> Registrati
            </button>
          </div>
        </div>

        {mode === "login" ? (
          <form
            onSubmit={doLogin}
            className="max-w-md mx-auto bg-white border border-[#E5E1DA] p-8 space-y-4"
            data-testid="login-form"
          >
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
                Email libreria
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none text-[#0B192C]"
                placeholder="libreria@esempio.it"
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none text-[#0B192C]"
                placeholder="••••••••"
                data-testid="login-password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-[#0B192C] text-[#F9F8F6] text-xs uppercase tracking-[0.2em] hover:bg-[#1E3557] disabled:opacity-60"
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="inline w-4 h-4 mr-2 animate-spin" />
              ) : (
                <BookMarked className="inline w-4 h-4 mr-2" />
              )}
              Accedi
            </button>
            <p className="text-xs text-center text-[#5A6472] pt-2">
              Nuova libreria?{" "}
              <button
                type="button"
                onClick={() => setMode("register")}
                className="text-[#0E7C8F] underline"
              >
                Registrati qui
              </button>
            </p>
          </form>
        ) : (
          <form
            onSubmit={doRegister}
            className="max-w-3xl mx-auto bg-white border border-[#E5E1DA] p-8"
            data-testid="register-form"
          >
            <p className="text-sm text-[#5A6472] mb-6">
              Registra la tua libreria per accedere all'area riservata. Riceverai
              le credenziali via email in pochi secondi. Il codice sconto 5% sarà
              generato automaticamente per te.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Nome libreria *" testid="reg-business_name" value={reg.business_name} onChange={(v) => setR("business_name", v)} required />
              <Field label="Referente *" testid="reg-contact_person" value={reg.contact_person} onChange={(v) => setR("contact_person", v)} required />
              <Field label="Email *" type="email" testid="reg-email" value={reg.email} onChange={(v) => setR("email", v)} required />
              <Field label="Telefono *" testid="reg-phone" value={reg.phone} onChange={(v) => setR("phone", v)} required />
              <Field label="Indirizzo *" testid="reg-address" value={reg.address} onChange={(v) => setR("address", v)} required className="md:col-span-2" />
              <Field label="Città *" testid="reg-city" value={reg.city} onChange={(v) => setR("city", v)} required />
              <Field label="CAP" testid="reg-postal_code" value={reg.postal_code} onChange={(v) => setR("postal_code", v)} />
              <Field label="Provincia" testid="reg-province" value={reg.province} onChange={(v) => setR("province", v)} />
              <Field label="Website" testid="reg-website" value={reg.website} onChange={(v) => setR("website", v)} />
              <Field label="P.IVA" testid="reg-vat_number" value={reg.vat_number} onChange={(v) => setR("vat_number", v)} />
              <Field label="Codice Fiscale" testid="reg-tax_code" value={reg.tax_code} onChange={(v) => setR("tax_code", v)} />
              <div className="md:col-span-2">
                <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">Note</label>
                <textarea
                  data-testid="reg-notes"
                  value={reg.notes}
                  onChange={(e) => setR("notes", e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none text-[#0B192C]"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="mt-6 w-full py-3 bg-[#E89B6C] text-[#0B192C] text-xs uppercase tracking-[0.2em] hover:bg-[#D4B895] disabled:opacity-60"
              data-testid="register-submit"
            >
              {loading ? (
                <Loader2 className="inline w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="inline w-4 h-4 mr-2" />
              )}
              Registra la Libreria
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[10px] uppercase tracking-[0.18em] text-[#5A6472] mb-2">
        {label}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-4 py-3 border border-[#E5E1DA] focus:border-[#0E7C8F] outline-none text-[#0B192C]"
      />
    </div>
  );
}
